import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface MultilingualSEOProps {
  pageType: 'city' | 'country' | 'home' | 'therapist';
  entityData?: any;
  currentLanguage?: string;
  supportedLanguages?: Array<{
    code: string;
    name: string;
    nativeName: string;
    region: string;
    direction: 'ltr' | 'rtl';
  }>;
  translatedContent?: {
    [languageCode: string]: {
      title: string;
      description: string;
      keywords: string[];
      h1: string;
      content?: string;
    };
  };
}

const MultilingualSEO: React.FC<MultilingualSEOProps> = ({
  pageType,
  entityData,
  currentLanguage = 'en',
  supportedLanguages = [
    { code: 'en', name: 'English', nativeName: 'English', region: 'US', direction: 'ltr' },
    { code: 'es', name: 'Spanish', nativeName: 'Español', region: 'ES', direction: 'ltr' },
    { code: 'fr', name: 'French', nativeName: 'Français', region: 'FR', direction: 'ltr' },
    { code: 'de', name: 'German', nativeName: 'Deutsch', region: 'DE', direction: 'ltr' },
    { code: 'it', name: 'Italian', nativeName: 'Italiano', region: 'IT', direction: 'ltr' },
    { code: 'pt', name: 'Portuguese', nativeName: 'Português', region: 'BR', direction: 'ltr' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', region: 'SA', direction: 'rtl' },
    { code: 'zh', name: 'Chinese', nativeName: '中文', region: 'CN', direction: 'ltr' },
    { code: 'ja', name: 'Japanese', nativeName: '日本語', region: 'JP', direction: 'ltr' },
    { code: 'ko', name: 'Korean', nativeName: '한국어', region: 'KR', direction: 'ltr' }
  ],
  translatedContent
}) => {
  const location = useLocation();
  const baseUrl = 'https://psychotherapist.help';
  
  // Generate hreflang URLs for all supported languages
  const generateHreflangUrls = () => {
    const currentPath = location.pathname;
    const hreflangUrls: Array<{ lang: string; url: string; region?: string }> = [];
    
    supportedLanguages.forEach(lang => {
      let url: string;
      
      if (lang.code === 'en') {
        // English is the default language (no prefix)
        url = `${baseUrl}${currentPath}`;
      } else {
        // Other languages use language prefix
        url = `${baseUrl}/${lang.code}${currentPath}`;
      }
      
      // Add language-region combination for better targeting
      hreflangUrls.push({
        lang: `${lang.code}-${lang.region}`,
        url,
        region: lang.region
      });
      
      // Add language-only version
      hreflangUrls.push({
        lang: lang.code,
        url
      });
    });
    
    // Add x-default pointing to English version
    hreflangUrls.push({
      lang: 'x-default',
      url: `${baseUrl}${currentPath}`
    });
    
    return hreflangUrls;
  };
  
  // Generate language-specific structured data
  const generateMultilingualStructuredData = () => {
    const currentLang = supportedLanguages.find(lang => lang.code === currentLanguage);
    const entityName = entityData?.name || 'Global Therapy Connect';
    
    const baseSchema = {
      "@context": "https://schema.org",
      "@type": pageType === 'city' ? "MedicalBusiness" : 
               pageType === 'country' ? "Service" : 
               pageType === 'therapist' ? "MedicalBusiness" : "Organization",
      "@id": `${baseUrl}${location.pathname}#multilingual`,
      "name": translatedContent?.[currentLanguage]?.title || entityName,
      "description": translatedContent?.[currentLanguage]?.description,
      "inLanguage": currentLanguage,
      "url": `${baseUrl}${location.pathname}`
    };
    
    // Add multilingual variants
    const multilingualSchema = {
      ...baseSchema,
      "availableLanguage": supportedLanguages.map(lang => ({
        "@type": "Language",
        "name": lang.name,
        "alternateName": lang.nativeName,
        "identifier": lang.code
      })),
      "sameAs": supportedLanguages
        .filter(lang => lang.code !== currentLanguage)
        .map(lang => {
          const url = lang.code === 'en' ? 
            `${baseUrl}${location.pathname}` : 
            `${baseUrl}/${lang.code}${location.pathname}`;
          return url;
        })
    };
    
    return multilingualSchema;
  };
  
  // Generate region-specific content schema
  const generateRegionalContentSchema = () => {
    if (pageType !== 'city' && pageType !== 'country') return null;
    
    const currentLang = supportedLanguages.find(lang => lang.code === currentLanguage);
    if (!currentLang || !entityData) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "WebPageElement",
      "@id": `${baseUrl}${location.pathname}#regional-content`,
      "name": `${entityData.name} Mental Health Services`,
      "description": `Professional therapy services in ${entityData.name} available in ${currentLang.nativeName}`,
      "inLanguage": currentLanguage,
      "about": {
        "@type": "Place",
        "name": entityData.name,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": entityData.name,
          "addressCountry": entityData.country
        }
      },
      "audience": {
        "@type": "Audience",
        "geographicArea": {
          "@type": pageType === 'city' ? "City" : "Country",
          "name": entityData.name
        },
        "inLanguage": currentLanguage
      },
      "provider": {
        "@type": "Organization",
        "name": "Global Therapy Connect",
        "availableLanguage": supportedLanguages.map(lang => lang.code)
      }
    };
  };
  
  // Generate cultural context schema for better local SEO
  const generateCulturalContextSchema = () => {
    const currentLang = supportedLanguages.find(lang => lang.code === currentLanguage);
    if (!currentLang || pageType !== 'city') return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      "@id": `${baseUrl}${location.pathname}#cultural-context`,
      "name": `Mental Health Services - ${currentLang.nativeName}`,
      "description": `Culturally appropriate mental health care in ${currentLang.nativeName} for ${entityData?.name} residents`,
      "inDefinedTermSet": {
        "@type": "DefinedTermSet",
        "name": "Multilingual Mental Health Services",
        "hasDefinedTerm": supportedLanguages.map(lang => ({
          "@type": "DefinedTerm",
          "name": `Mental Health - ${lang.nativeName}`,
          "identifier": lang.code,
          "inLanguage": lang.code
        }))
      }
    };
  };
  
  const hreflangUrls = generateHreflangUrls();
  const multilingualSchema = generateMultilingualStructuredData();
  const regionalContentSchema = generateRegionalContentSchema();
  const culturalContextSchema = generateCulturalContextSchema();
  const currentLang = supportedLanguages.find(lang => lang.code === currentLanguage);
  
  return (
    <Helmet>
      {/* HTML lang attribute */}
      <html lang={currentLanguage} dir={currentLang?.direction || 'ltr'} />
      
      {/* Content language meta */}
      <meta httpEquiv="content-language" content={currentLanguage} />
      <meta name="language" content={currentLanguage} />
      
      {/* Hreflang tags for all language variants */}
      {hreflangUrls.map(({ lang, url }, index) => (
        <link
          key={`hreflang-${lang}-${index}`}
          rel="alternate"
          hrefLang={lang}
          href={url}
        />
      ))}
      
      {/* Language-specific Open Graph tags */}
      <meta property="og:locale" content={`${currentLanguage}_${currentLang?.region || 'US'}`} />
      {supportedLanguages
        .filter(lang => lang.code !== currentLanguage)
        .slice(0, 5) // Limit to prevent too many meta tags
        .map(lang => (
          <meta
            key={`og-locale-alt-${lang.code}`}
            property="og:locale:alternate"
            content={`${lang.code}_${lang.region}`}
          />
        ))}
      
      {/* Language navigation schema */}
      <script type="application/ld+json">
        {JSON.stringify(multilingualSchema)}
      </script>
      
      {/* Regional content schema */}
      {regionalContentSchema && (
        <script type="application/ld+json">
          {JSON.stringify(regionalContentSchema)}
        </script>
      )}
      
      {/* Cultural context schema */}
      {culturalContextSchema && (
        <script type="application/ld+json">
          {JSON.stringify(culturalContextSchema)}
        </script>
      )}
      
      {/* Language-specific meta keywords */}
      {translatedContent?.[currentLanguage]?.keywords && (
        <meta 
          name="keywords" 
          content={translatedContent[currentLanguage].keywords.join(', ')} 
        />
      )}
      
      {/* Alternative language versions for RSS/XML */}
      {supportedLanguages.map(lang => (
        <link
          key={`rss-${lang.code}`}
          rel="alternate"
          type="application/rss+xml"
          title={`Mental Health Resources - ${lang.nativeName}`}
          href={`${baseUrl}/${lang.code}/rss.xml`}
        />
      ))}
      
      {/* Language-specific canonical with proper language prefix */}
      <link 
        rel="canonical" 
        href={currentLanguage === 'en' ? 
          `${baseUrl}${location.pathname}` : 
          `${baseUrl}/${currentLanguage}${location.pathname}`
        } 
      />
      
      {/* Preload alternate language versions for better UX */}
      {supportedLanguages
        .filter(lang => lang.code !== currentLanguage)
        .slice(0, 3) // Preload top 3 alternative languages
        .map(lang => {
          const url = lang.code === 'en' ? 
            `${baseUrl}${location.pathname}` : 
            `${baseUrl}/${lang.code}${location.pathname}`;
          return (
            <link
              key={`preload-${lang.code}`}
              rel="prefetch"
              href={url}
            />
          );
        })}
      
      {/* RTL-specific styles for Arabic and other RTL languages */}
      {currentLang?.direction === 'rtl' && (
        <link rel="stylesheet" href="/styles/rtl.css" />
      )}
      
      {/* Language-specific font optimization */}
      {currentLanguage === 'ar' && (
        <link 
          rel="preload" 
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;700&display=swap" 
          as="style" 
        />
      )}
      {currentLanguage === 'zh' && (
        <link 
          rel="preload" 
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;700&display=swap" 
          as="style" 
        />
      )}
      {currentLanguage === 'ja' && (
        <link 
          rel="preload" 
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;700&display=swap" 
          as="style" 
        />
      )}
      {currentLanguage === 'ko' && (
        <link 
          rel="preload" 
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;700&display=swap" 
          as="style" 
        />
      )}
    </Helmet>
  );
};

export default MultilingualSEO;
